import { useCallback, useImperativeHandle, useRef, forwardRef, RefObject, Ref, memo, Children, cloneElement, ReactElement } from 'react';
import { useTableResize } from '@/hooks';
import { useMount } from 'ahooks';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import { PAGE_PROPS } from './constants';
import { defaultPageSize } from '@/config';
import snakeCase from 'lodash/snakeCase';
import { PaginationProps, PaginationRef, ElTable, ElPagination, TableProps, TableRef } from '@qsxy/element-plus-react';
import { isEmpty } from 'lodash';

export interface KrmTableRef {
	resize: () => void;
}

interface Props<T> extends Omit<TableProps<T>, 'instance'>, Omit<PaginationProps, 'size' | 'instance' | 'style' | 'instance'> {
	/** 工具栏 */
	toolbar?: ReactElement<any>;
	/** 用来计算表格的高度，此高度为表格下方内容占据高度，总高度减去此高度以及表格上方的高度（自动获取）即为表格高度 */
	delHeight?: number;
	/** 数据总量 */
	total: number;
	/** 分页实例 */
	pageRef: RefObject<PaginationRef>;
	/** 查询方法 */
	onQuery: (params: { pageNum?: number; pageSize?: number } & Record<string, any>) => void;
	/** 子组件 */
	children: TableProps<T>['children'];
	instance?: RefObject<KrmTableRef>;
	sortable?: boolean;
}

const getWidth = (name: string | ReactElement<{}>) => {
	if (typeof name === 'string') {
		const len = name.length;
		if (len > 0) {
			return 80 + (len - 2) * 20;
		}
		return 100;
	}
	return 100;
};

const orderMap = {
	ascending: 'asc',
	descending: 'desc',
};

/**
 * 有分页的表格，且可以传入工具栏
 */
function InternalTable<RecordType extends object>(props: Props<RecordType>, ref: RefObject<TableRef<RecordType>>) {
	const { toolbar, total, onQuery, children, headerCellStyle, pageRef, delHeight, instance, sortable = true, ...other } = props;
	const pageProps: Pick<
		PaginationProps,
		| 'current'
		| 'defaultCurrent'
		| 'disabled'
		| 'defaultPageSize'
		| 'pageSize'
		| 'pageSizeOptions'
		| 'hideOnSinglePage'
		| 'showPrevNextJumpers'
		| 'showLessItems'
		| 'showTitle'
		| 'onShowSizeChange'
		| 'itemRender'
		| 'simple'
	> = pick(other, PAGE_PROPS);
	const tableRef = useRef<TableRef<RecordType>>(null);
	const tableContainerRef = useRef<HTMLDivElement>(null);

	const { resizeFn } = useTableResize(tableContainerRef, tableRef, delHeight, isEmpty(props.height || props.maxHeight));

	const showTotal = useCallback((t: number, [from, to]: [number, number]) => {
		return `显示第 ${from} 到第 ${to} 条记录，总共 ${t} 条记录`;
	}, []);

	useMount(() => {
		setTimeout(() => {
			tableRef.current?.doLayout();
		}, 350);
	});

	useImperativeHandle(ref, () => tableRef.current);
	useImperativeHandle(instance, () => ({ resize: resizeFn }));

	return (
		<div className="ttn-main-table">
			{toolbar}

			<div className="ttn-table-container" ref={tableContainerRef}>
				<ElTable<RecordType>
					ref={tableRef}
					{...omit(other, PAGE_PROPS)}
					onSortChange={({ prop, order }) => {
						if (order) {
							onQuery({ prop: snakeCase(prop).toLocaleUpperCase(), order: orderMap[order] });
						} else {
							onQuery({});
						}
					}}
				>
					{Children.map(children, child =>
						cloneElement(child, {
							minWidth: getWidth(child.props?.label),
							showOverflowTooltip: child.props?.type !== 'selection',
							sortable: !sortable || ['selection', 'index'].includes(child.props?.type) || isEmpty(child.props?.prop) ? false : 'custom',
							...child.props,
						}),
					)}
				</ElTable>
			</div>

			<div className="ttn-table-pagination">
				<ElPagination
					ref={pageRef}
					total={total}
					showTotal={() => false}
					showQuickJumper
					showSizeChanger={false}
					defaultPageSize={defaultPageSize}
					pageSizeOptions={[defaultPageSize + '', '20', '50', '100', '1000']}
					onChange={(pageNum, pageSize) => onQuery?.({ pageNum, pageSize })}
					{...pageProps}
				/>
			</div>
		</div>
	);
}

const Table = memo(forwardRef(InternalTable)) as <RecordType extends object = any>(props: Props<RecordType> & { ref?: Ref<TableRef<RecordType>> }) => ReactElement;

export default Table;
